/**
 * <footer-localization-form> — deterministic Shopify localization submission.
 *
 * Public DOM contract:
 * - Wraps one Shopify `{% form 'localization' %}`.
 * - `[data-localization-input]` is a disabled hidden input whose name matches
 *   the option buttons (`country_code` or `locale_code`).
 * - `[data-localization-option]` is a native submit button carrying the
 *   selected value.
 *
 * With JavaScript, the selected value is copied to the hidden input before
 * submitting the form directly. The submitter is therefore not required in
 * the serialized payload, which keeps selection reliable on iOS. Without
 * JavaScript, the disabled input is omitted and the native submit button
 * remains the successful form control.
 */
class FooterLocalizationForm extends HTMLElement {
  #form = null;
  #input = null;
  #onClick = null;

  connectedCallback() {
    if (this.#onClick) return;

    this.#form = this.querySelector('form');
    this.#input = this.querySelector('[data-localization-input]');
    if (!this.#form || !this.#input) return;

    this.#onClick = (event) => {
      const option = event.target.closest?.('[data-localization-option]');
      if (!option || !this.contains(option) || option.form !== this.#form) return;
      if (!option.value || option.name !== this.#input.name) return;

      event.preventDefault();
      this.#input.value = option.value;
      this.#input.disabled = false;
      this.#form.submit();
    };

    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    if (this.#onClick) {
      this.removeEventListener('click', this.#onClick);
      this.#onClick = null;
    }

    this.#form = null;
    this.#input = null;
  }
}

if (!customElements.get('footer-localization-form')) {
  customElements.define('footer-localization-form', FooterLocalizationForm);
}
